/**
 * Adaptateur audio (D-20). Synchronisation par décalage d'horloge mesuré avec
 * le serveur ; si l'incertitude dépasse 250 ms, on retombe automatiquement sur
 * une lecture locale non synchronisée, sans intervention.
 */

let ctx: AudioContext | null = null;
let offsetMs = 0;
let uncertaintyMs = Number.POSITIVE_INFINITY;

/**
 * Une seconde de silence, servie avec l'application (`public/silence.wav`).
 *
 * Sur iPhone, un contexte Web Audio seul reste soumis à l'**interrupteur
 * silencieux** : le motif tourne, l'appareil ne sort rien, et personne ne
 * comprend pourquoi. Lire une piste par un élément `<audio>` fait basculer la
 * session audio du système en catégorie « lecture », et le son passe alors,
 * silencieux physique compris. Il faut que cela parte du même geste que le
 * déverrouillage : c'est le rôle du portail « Rejoindre le groove ».
 */
const SILENCE = "/silence.wav";

let porteur: HTMLAudioElement | null = null;

function ouvrirLaSessionAudio(): void {
  try {
    if (!porteur) {
      porteur = new Audio(SILENCE);
      porteur.loop = true;
      porteur.volume = 1; // la piste est muette : le volume ne fait que déclarer l'intention
      porteur.setAttribute("playsinline", "");
    }
    void porteur.play().catch(() => undefined);
  } catch {
    /* pas d'élément audio : on se contentera du contexte */
  }
}

/** Doit être appelé depuis un geste de l'utilisateur : iOS l'exige. */
export async function unlockAudio(): Promise<boolean> {
  try {
    const AC = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AC) return false;
    ouvrirLaSessionAudio();
    ctx = ctx ?? new AC();
    if (ctx.state === "suspended") await ctx.resume();
    // Un son inaudible mais réel : certains iPhone n'ouvrent la sortie qu'après
    // avoir effectivement joué quelque chose depuis le geste de l'utilisateur.
    reveiller();
    return ctx.state === "running";
  } catch {
    return false;
  }
}

/** Une impulsion inaudible, pour que la sortie s'ouvre vraiment. */
function reveiller(): void {
  if (!ctx) return;
  const g = ctx.createGain();
  g.gain.value = 0.0001;
  const o = ctx.createOscillator();
  o.frequency.value = 440;
  o.connect(g).connect(ctx.destination);
  o.start();
  o.stop(ctx.currentTime + 0.05);
}

/**
 * iOS suspend le contexte dès que l'application passe en arrière-plan — ce qui
 * arrive à chaque verrouillage d'écran pendant l'approche. On le relance au
 * retour, sinon le motif reste muet jusqu'à la fin de la marche.
 */
if (typeof document !== "undefined") {
  const relancer = (): void => {
    if (ctx && ctx.state === "suspended") void ctx.resume().catch(() => undefined);
    if (porteur && porteur.paused) void porteur.play().catch(() => undefined);
  };
  document.addEventListener("visibilitychange", () => {
    if (!document.hidden) relancer();
  });
  window.addEventListener("pageshow", relancer);
  window.addEventListener("focus", relancer);
}

/** Le contexte, une fois déverrouillé. Null tant que l'utilisateur n'a rien touché. */
export function contexteAudio(): AudioContext | null {
  return ctx && ctx.state === "running" ? ctx : null;
}

export function setClock(samples: Array<{ rtt: number; offset: number }>): void {
  if (samples.length === 0) return;
  const sorted = [...samples].sort((a, b) => a.rtt - b.rtt);
  const best = sorted[0]!;
  offsetMs = best.offset;
  uncertaintyMs = best.rtt / 2;
}

export const synchronised = () => uncertaintyMs <= 250;
export const serverNow = () => Date.now() + offsetMs;

/** Une note courte. Le timbre définitif viendra avec les motifs sonores. */
export function tone(freq: number, durationMs = 180, gain = 0.05): void {
  if (!ctx || ctx.state !== "running") return;
  const t = ctx.currentTime;
  const osc = ctx.createOscillator();
  const g = ctx.createGain();
  osc.frequency.value = freq;
  osc.type = "sine";
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(gain, t + 0.02);
  g.gain.exponentialRampToValueAtTime(0.0001, t + durationMs / 1000);
  osc.connect(g).connect(ctx.destination);
  osc.start(t);
  osc.stop(t + durationMs / 1000 + 0.02);
}

/**
 * Ancien signal ponctuel par palier. Remplacé par le motif continu
 * (`adapters/groove.ts`) : le son ne prévient plus d'un franchissement, il
 * accompagne toute la marche. Conservé pour les repères courts hors approche.
 */
export function bandCue(band: "far" | "approach" | "near" | "here"): void {
  const map = { far: 220, approach: 294, near: 392, here: 523 } as const;
  tone(map[band], band === "here" ? 420 : 160, band === "here" ? 0.08 : 0.04);
  if (band === "here") setTimeout(() => tone(659, 420, 0.08), 140);
}

/**
 * Trois notes franches, pour vérifier qu'un téléphone sort vraiment du son.
 * Volontairement plus fort que le motif d'approche : la question posée est
 * « est-ce que j'entends quelque chose », pas « est-ce que c'est joli ».
 */
export function essaiDeSon(): void {
  if (!ctx || ctx.state !== "running") return;
  const t0 = ctx.currentTime + 0.05;
  [440, 587, 880].forEach((freq, i) => {
    const osc = ctx!.createOscillator();
    const g = ctx!.createGain();
    const t = t0 + i * 0.18;
    osc.type = "triangle";
    osc.frequency.value = freq;
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.22, t + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.22);
    osc.connect(g).connect(ctx!.destination);
    osc.start(t);
    osc.stop(t + 0.25);
  });
}
